import { BankOutlined, RightOutlined } from "@ant-design/icons"
import { Spin, Typography } from "antd"
import React, { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import { isSepaPaymentMethod } from "../../helpers/isSepaPaymentMethod"
import { useCurrency } from "../../helpers/currencyHelper"
import { CurrencyShort } from "../../helpers/renderAmount"
import { renderAsMoney } from "../../helpers/renderAsMoney"
import { getStoredBookerId } from "../../helpers/getStoredBookerId"
import { useGetUpdatedBookerQuery } from "../../generated/graphql"
import { URL_ACCOUNT_PAYMENT, URL_ACCOUNT_PROFILE, URL_REGISTER_SEPA_EDIT_ID } from "../../models/url"
import { SubHeader, maskIban } from "./AccountDashboard"
import css from "./AccountPayment.module.css"

const { Text } = Typography

const AccountPayment: FC = () => {
    const { t } = useTranslation()
    const currency = useCurrency().short as CurrencyShort
    const bookerId = getStoredBookerId()

    const { data, loading, error, refetch } = useGetUpdatedBookerQuery({
        skip: !bookerId,
        variables: { id: bookerId ?? "" },
        fetchPolicy: "cache-and-network",
    })

    const booker = data?.booker
    const title = t("account.payment.title", "Zahlung & Guthaben")

    if (loading && !booker) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <div className={css.loading} data-testid='account-payment-loading'>
                    <Spin spinning />
                </div>
            </div>
        )
    }

    if (error && !booker) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <QueryErrorAlert testId='account-payment-error' message={t("account.loadError", "Dein Benutzerkonto konnte nicht geladen werden.")} onRetry={() => refetch()} />
            </div>
        )
    }

    if (!booker) return null

    const credit = booker.credit ?? 0
    const sepa = booker.paymentMethods?.find(isSepaPaymentMethod)
    // Router-State fromUrl bringt den Nutzer nach Speichern/Abbrechen hierher zurück
    // (SepaDataEdit/SepaDebitEditForm navigieren zu fromUrl ?? URL_ACCOUNT).
    const editUrl = sepa ? URL_REGISTER_SEPA_EDIT_ID.replace(":id", sepa.id) : ""

    return (
        <div className={css.page} data-testid='account-payment'>
            <SubHeader title={title} />

            <section className={css.section} data-testid='account-payment-credit'>
                <div className={css.sectionTitle}>{t("account.credit", "Guthaben")}</div>
                {credit > 0 ? (
                    <div className={css.creditValue}>{renderAsMoney(credit, "", currency)}</div>
                ) : (
                    <Text type='secondary'>{t("account.payment.creditNone", "Kein Guthaben vorhanden")}</Text>
                )}
                {credit > 0 && <div className={css.hint}>{t("account.payment.creditHint", "Guthaben wird bei der nächsten Buchung verrechnet.")}</div>}
            </section>

            <section className={css.section} data-testid='account-payment-sepa'>
                <div className={css.sectionTitle}>{t("account.payment.sepaTitle", "Lastschrift (SEPA)")}</div>
                {sepa ? (
                    <>
                        <dl className={css.list}>
                            <dt>{t("account.payment.accountHolder", "Kontoinhaber")}</dt>
                            <dd>{sepa.sepaAccountholder || "—"}</dd>
                            <dt>{t("account.payment.bank", "Bank")}</dt>
                            <dd>{sepa.bank || "—"}</dd>
                            <dt>{t("account.payment.iban", "IBAN")}</dt>
                            <dd>{maskIban(sepa.iban) || "—"}</dd>
                        </dl>
                        <Link
                            to={{ pathname: editUrl, state: { fromUrl: { pathname: URL_ACCOUNT_PAYMENT }, bookerId } }}
                            className={css.action}
                            data-testid='account-payment-edit'
                        >
                            {t("account.payment.edit", "Bearbeiten")}
                        </Link>
                    </>
                ) : (
                    <>
                        <Text type='secondary'>{t("account.payment.noSepa", "Es sind keine Lastschriftdaten hinterlegt.")}</Text>
                        <Link
                            // fromUrl: Profil-Zurück-Pfeil führt hierher zurück statt zum Dashboard
                            to={{ pathname: URL_ACCOUNT_PROFILE, state: { openSepaCreate: true, fromUrl: { pathname: URL_ACCOUNT_PAYMENT } } }}
                            className={css.addRow}
                            data-testid='account-payment-add-sepa'
                        >
                            <BankOutlined className={css.addIcon} />
                            <span className={css.addLabel}>{t("account.payment.addSepa", "Jetzt hinterlegen")}</span>
                            <RightOutlined className={css.chevron} />
                        </Link>
                    </>
                )}
            </section>
        </div>
    )
}

export default AccountPayment
